/**
 * Tabella movimenti di un articolo di magazzino.
 *
 * Usata nella pagina ArticoloDetail come contenuto del tab "Movimenti".
 * Mostra lo storico movimenti dell'anno corrente per l'articolo specificato,
 * con quantità colorata e badge tipo.
 *
 * Importa esclusivamente da @floraltech/ui (regola 6.8 CLAUDE.md).
 */

import {
  Badge,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@floraltech/ui';
import React, { useCallback, useEffect, useState } from 'react';
import { useFormatters } from '@lib/formatters';

interface Movimento {
  id: string;
  created_at: string;
  tipo: string;
  quantita: number;
  riferimento_esterno: string | null;
  note: string | null;
}

interface MovimentiTableProps {
  /** UUID dell'articolo di magazzino. Opzionale se si filtra per lotto. */
  articoloId?: string;
  /** UUID del lotto. Opzionale, filtra i movimenti per lotto specifico. */
  lottoId?: string;
}

/**
 * Tabella movimenti dell'articolo con quantità colorata e badge tipo.
 */
const MovimentiTable: React.FC<MovimentiTableProps> = ({
  articoloId,
  lottoId,
}: MovimentiTableProps) => {
  const fmt = useFormatters();
  const [movimenti, setMovimenti] = useState<Movimento[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /** Anno corrente derivato dal sistema, non hardcoded (DEC-018). */
  const currentYear = new Date().getFullYear();

  const carica = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (articoloId) params.set('articolo_id', articoloId);
      if (lottoId) params.set('lotto_id', lottoId);
      params.set('year', String(currentYear));
      const response = await fetch(
        `/api/v1/magazzino/movimenti?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
          },
        },
      );
      if (!response.ok) throw new Error(`Errore ${response.status}`);
      const data: Movimento[] = await response.json();
      setMovimenti(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setLoading(false);
    }
  }, [articoloId, lottoId, currentYear]);

  useEffect(() => {
    void carica();
  }, [carica]);

  const formatDateTime = (iso: string): string => fmt.dateTime(iso);

  return (
    <Card>
      <CardContent className="p-0">
        {loading && (
          <p className="p-6 text-center text-sm text-muted-foreground">
            Caricamento…
          </p>
        )}
        {error && (
          <p className="p-6 text-center text-sm text-destructive">{error}</p>
        )}
        {!loading && !error && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead className="text-right">Quantità</TableHead>
                <TableHead>Riferimento</TableHead>
                <TableHead>Note</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {movimenti.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="text-center text-sm text-muted-foreground"
                  >
                    Nessun movimento per questo articolo
                  </TableCell>
                </TableRow>
              )}
              {movimenti.map((mov: Movimento) => (
                <TableRow key={mov.id}>
                  <TableCell className="text-sm">
                    {formatDateTime(mov.created_at)}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{mov.tipo}</Badge>
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    <span
                      className={
                        mov.quantita > 0
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-destructive'
                      }
                    >
                      {mov.quantita > 0 ? `+${mov.quantita}` : mov.quantita}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm">
                    {mov.riferimento_esterno ?? '—'}
                  </TableCell>
                  <TableCell className="text-sm">
                    {mov.note ?? '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};

export default MovimentiTable;
